// Constants
import {
  INPUT_OTP_INPUT_PROPS,
  ONE_SECOND_MILIS,
  OTP_RETRY_TIMEOUT_KEY,
  OTP_RETRY_TIMEOUT_MILLIS,
} from '../../constants/contract-otp.constants';
// Resolvers
import { getOtpStateResolver } from '../../resolvers/contract-otp.resolver';
// Translations
import translations from '../../translations/contract-otp.translations';
// Types
import {
  GetOptInfoHandlerReturnType,
  GetOptInfoHandlerType,
} from '../types/contract-otp.handlers.type';
import {
  GetVerifyMessageType,
  IsAttemptsAvailableType,
  IsAttemptsNumberKnownType,
  IsNoMoreAttemptsType,
  IsShowAttemptsWarningType,
  IsVerifyButtonDisabledType,
  SetOtpRequestTimeoutType,
} from './types/contract-otp.handlers.utils.type';
// Utils
import {
  isNoMoreVerifyAttempts,
  isRemainingVerifyAttempts,
  isVerifyLastChance,
} from './utils/contract-otp.handlers.utils.utils';

const getOtpDelay = (): number => {
  const storedDate = localStorage.getItem(OTP_RETRY_TIMEOUT_KEY);
  return !storedDate
    ? 0
    : Math.ceil(
        Math.max(
          new Date(storedDate).getTime() + OTP_RETRY_TIMEOUT_MILLIS - new Date().getTime(),
          0
        ) / ONE_SECOND_MILIS
      );
};

const isNoMoreAttempts = ({ otpInfo, verifyInfo }: IsNoMoreAttemptsType): boolean =>
  otpInfo.attempts !== null &&
  verifyInfo.attempts !== null &&
  otpInfo.attempts <= 0 &&
  verifyInfo.attempts <= 0;

export const getOtpInfo = async ({
  formatMessage,
  setError,
  setOtpInfo,
  setPhone,
  setVerifyInfo,
}: GetOptInfoHandlerType): GetOptInfoHandlerReturnType => {
  setOtpInfo(info => ({ ...info, disabled: true }));
  setVerifyInfo(info => ({ ...info, fetching: true }));
  const [response, error] = await getOtpStateResolver();
  setOtpInfo(info => ({ ...info, disabled: false }));
  setVerifyInfo(info => ({ ...info, fetching: false }));
  if (!error) {
    setPhone?.(response.phone);
    setOtpInfo(info => ({
      ...info,
      attempts: response.attemptsRemainingSend,
      id: response.otpId,
    }));
    setVerifyInfo(info => ({
      ...info,
      attempts: response.attemptsRemainingVerify,
      showAttemptsWarning: isShowAttemptsWarning({ response, verifyInfo: info }),
      msg: getVerifyMessage({
        formatMessage,
        otpAttempts: response.attemptsRemainingSend,
        verifyAttempts: response.attemptsRemainingVerify,
      }),
    }));
    return {
      otpAttempts: response.attemptsRemainingSend,
      verifyAttempts: response.attemptsRemainingVerify,
    };
  }
  setError(true);
  return {
    otpAttempts: null,
    verifyAttempts: null,
  };
};

const getVerifyMessage = ({
  formatMessage,
  otpAttempts,
  verifyAttempts,
}: GetVerifyMessageType): string => {
  let msg;
  if (isVerifyLastChance({ otpAttempts, verifyAttempts })) {
    msg = translations.OTP_VERIFY_LAST_CHANCE;
  } else if (isNoMoreVerifyAttempts(verifyAttempts)) {
    msg = translations.OTP_VERIFY_REQUEST_NEW_CODE;
  } else if (isRemainingVerifyAttempts(verifyAttempts)) {
    msg = translations.OTP_VERIFY_ATTEMPTS;
  } else {
    msg = translations.OTP_VERIFY_ERROR;
  }
  return formatMessage({ id: msg }, { attempts: verifyAttempts });
};

const isShowAttemptsWarning = ({ response, verifyInfo }: IsShowAttemptsWarningType): boolean =>
  verifyInfo.showAttemptsWarning ||
  response.attemptsRemainingVerify <= 0 ||
  (response.attemptsRemainingVerify === 1 && response.attemptsRemainingSend === 0);

const setOtpRequestTimeout = ({ setLastOtpRequestTimestamp }: SetOtpRequestTimeoutType): void => {
  const now = new Date().toJSON();
  setLastOtpRequestTimestamp(now);
  localStorage.setItem(OTP_RETRY_TIMEOUT_KEY, now);
};

const isVerifyButtonDisabled = ({ verifyInfo, otpInfo }: IsVerifyButtonDisabledType): boolean =>
  verifyInfo.attempts === null ||
  otpInfo.code.length !== INPUT_OTP_INPUT_PROPS.maxLength ||
  verifyInfo.attempts <= 0;

const isAttemptsNumberKnown = ({ attempts }: IsAttemptsNumberKnownType): boolean =>
  attempts !== null;

const isAttemptsAvailable = (otpInfo: IsAttemptsAvailableType): boolean =>
  isAttemptsNumberKnown(otpInfo) && (otpInfo.attempts as number) > 0;

const isDelayExpired = (delay: number): boolean => delay <= 0;

export {
  getOtpDelay,
  getVerifyMessage,
  isAttemptsAvailable,
  isAttemptsNumberKnown,
  isDelayExpired,
  isNoMoreAttempts,
  isShowAttemptsWarning,
  isVerifyButtonDisabled,
  setOtpRequestTimeout,
};
